import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { Address } from '@vsolv/core/address/domain';
import type { PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PaginationConfig, SortParams, TableColumn, TextColumn } from '@vsolv/vectors/table';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { BatchPayment } from '@wsphere/warranties/domain';
import { CheckboxColumn } from '../../components';
import { PayBatchDialog } from '../../dialogs';
import {
  BatchPaymentService,
  describeBatchPaymentError,
  UnfundedFilterPolicy,
  UnfundedWarrantiesService,
  UnfundedWarranty,
} from '../../services';

const GENERIC_ERROR = 'Something went wrong with this batch payment. Please try again.';

type DateRangeInput = Date | { startDate: Date | null; endDate: Date | null } | null;

// Enough to total the batch and name its rows; a submitted batch hydrates from the server, not the list.
type SelectedWarranty = Pick<UnfundedWarranty, 'id' | 'contractNumber' | 'netAmountDue'>;

interface UnfundedFilters {
  policyId: string | null;
  planId: string | null;
  startDate: Date | null;
  endDate: Date | null;
}

const NO_FILTERS: UnfundedFilters = { policyId: null, planId: null, startDate: null, endDate: null };

// vs-table emits the column header as `sortBy`; map our labels onto the handler's columnMap keys.
const SORT_KEY_BY_HEADER: Record<string, string> = {
  'Warranty #': 'Contract Number',
  Buyer: 'Customer',
  Policy: 'Policy',
  Plan: 'Warranties',
  'Net Amount Due': 'Net Amount Due',
  'Date Provisioned': 'Created',
};

const EMPTY_RESPONSE: PaginationQueryResponse<UnfundedWarranty> = {
  items: [],
  meta: { itemsPerPage: 10, itemCount: 0, currentPage: 1, totalItems: 0, totalPages: 0 },
};

// Retail is stored in cents, and comes back as a string from the raw decimal column.
function formatCents(cents: number | string | undefined) {
  return (Number(cents ?? 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Null means rating never priced the warranty — show a dash, never $0.00.
function formatNetAmountDue(cents: number | string | null | undefined) {
  return cents === null || cents === undefined ? '—' : formatCents(cents);
}

// An unpriced warranty cannot be charged — the create handler rejects the whole batch over one.
function isPayable(warranty: SelectedWarranty) {
  return warranty.netAmountDue !== null && warranty.netAmountDue !== undefined;
}

function describeUnpayable(warranty: SelectedWarranty) {
  return isPayable(warranty) ? undefined : 'This warranty has no net amount due yet, so it cannot be paid.';
}

// Default CD like the other vs-table pages — cells resolve their content on a microtask, so OnPush
// renders empty rows.
@Component({
  selector: 'ws-distributor-unfunded-warranties-page',
  templateUrl: './unfunded-warranties.page.html',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class DistributorUnfundedWarrantiesPage implements OnDestroy {
  private unfundedSvc = inject(UnfundedWarrantiesService);
  private batchSvc = inject(BatchPaymentService);

  @Input() distributorId: string | null = null;
  @PropertyListener('distributorId') private distributorId$ = new ReplaySubject<string | null>(1);

  /** Seeds the payment dialog's billing address. */
  @Input() distributorAddress: Address.Model | null = null;

  @ViewChild(PayBatchDialog) private paymentDialog?: PayBatchDialog;

  protected error$ = new BehaviorSubject(false);

  // Only the first load shows the skeleton; later pages/sorts keep the current rows on screen.
  protected isInitialLoading = true;

  protected policies: UnfundedFilterPolicy[] = [];

  readonly pagination$ = new BehaviorSubject({ limit: 10, page: 1 });
  readonly sort$ = new BehaviorSubject<{ sortBy?: string; sortOrder?: 'ASC' | 'DESC' }>({
    sortBy: 'Created',
    sortOrder: 'DESC',
  });
  readonly filters$ = new BehaviorSubject<UnfundedFilters>(NO_FILTERS);

  readonly itemsPerPage = this.pagination$.value.limit;

  // Keyed by id and holding the row, so the running total survives paging away from a selection.
  readonly selected$ = new BehaviorSubject(new Map<string, SelectedWarranty>());

  // Raised when a tick was refused for hitting the cap; cleared by the next successful change.
  readonly limitReached$ = new BehaviorSubject(false);

  // The seller's live draft. Its presence locks the selection until it is paid or cancelled.
  readonly openBatch$ = new BehaviorSubject<BatchPayment.Model | null>(null);
  readonly submitError$ = new BehaviorSubject<string | null>(null);

  protected isSubmitting = false;

  readonly maxBatchSize = BatchPayment.MAX_BATCH_SIZE;

  // A batch is created whole and locked, so nothing can be ticked while one is in progress. The reason
  // has to match the banner: a draft can still be taken apart, a submitted one is committed to a charge
  // and has no Change Selection button to point at.
  private lockReason$ = this.openBatch$.pipe(
    map(batch => {
      if (!batch) return null;

      return batch.status === BatchPayment.Status.SUBMITTED
        ? 'A payment is in progress for these warranties. Finish or retry that payment before changing the selection.'
        : 'These warranties are reserved for payment. Use Change Selection to pick different ones.';
    })
  );

  // A draft can still be taken apart; a submitted one is committed to its payment and only cancel
  // through the payment itself. The two states offer different actions, so the view splits on them.
  protected isDraftBatch$ = this.openBatch$.pipe(map(batch => batch?.status === BatchPayment.Status.DRAFT));
  protected isSubmittedBatch$ = this.openBatch$.pipe(map(batch => batch?.status === BatchPayment.Status.SUBMITTED));

  columns: TableColumn<unknown>[] = [
    new CheckboxColumn<UnfundedWarranty>({ fitContent: true }, warranty => ({
      state$: combineLatest([this.selected$, this.lockReason$]).pipe(
        map(([selected, lockReason]) => ({
          checked: selected.has(warranty.id),
          disabled: !!lockReason || !isPayable(warranty),
          // The lock wins: while a batch is open it is why the row cannot be ticked, priced or not.
          tooltip: lockReason ?? describeUnpayable(warranty),
        }))
      ),
      ariaLabel: `Select warranty ${warranty.contractNumber}`,
      toggled: () => this.toggleWarranty(warranty),
    })),

    new TextColumn<UnfundedWarranty>({ header: 'Warranty #', sortable: true }, warranty => ({
      text: warranty.contractNumber,
      classes: 'font-medium',
    })),

    new TextColumn<UnfundedWarranty>({ header: 'Buyer', sortable: true }, warranty => ({
      text: warranty.customer?.name ?? '—',
    })),

    new TextColumn<UnfundedWarranty>({ header: 'Policy', sortable: true }, warranty => ({
      text: warranty.policy?.title ?? '—',
    })),

    new TextColumn<UnfundedWarranty>({ header: 'Plan', sortable: true }, warranty => ({
      text: warranty.plan?.title ?? '—',
    })),

    // Not sortable — the list handler has no retail sort key.
    new TextColumn<UnfundedWarranty>({ header: 'Retail Price', align: 'right', fitContent: true }, warranty => ({
      text: formatCents(warranty.retail),
    })),

    new TextColumn<UnfundedWarranty>(
      { header: 'Net Amount Due', sortable: true, align: 'right', fitContent: true },
      warranty => ({ text: formatNetAmountDue(warranty.netAmountDue), classes: 'font-medium' })
    ),

    new TextColumn<UnfundedWarranty>(
      { header: 'Date Provisioned', sortable: true, sort: 'DESC', fitContent: true },
      warranty => ({ text: moment(warranty.created).format('MMM DD, YYYY') })
    ),
  ];

  private data$ = this.distributorId$.pipe(
    distinctUntilChanged(),
    tap(distributorId => {
      this.loadFilterPolicies();
      this.loadOpenBatch(distributorId);
    }),
    switchMap(distributorId =>
      combineLatest([this.pagination$, this.sort$, this.filters$]).pipe(
        map(([pagination, sort, filters]) => ({ ...pagination, ...sort, filters, distributorId }))
      )
    ),
    tap(() => this.error$.next(false)),
    switchMap(({ distributorId, filters, ...params }) => {
      if (!distributorId) return of(EMPTY_RESPONSE);

      return this.unfundedSvc
        .list$({
          distributorId,
          ...params,
          policyIds: filters.policyId ? [filters.policyId] : undefined,
          planIds: filters.planId ? [filters.planId] : undefined,
          provisionedStartDate: filters.startDate,
          provisionedEndDate: filters.endDate,
        })
        .pipe(
          catchError(() => {
            this.error$.next(true);
            return of(EMPTY_RESPONSE);
          })
        );
    }),
    tap(() => (this.isInitialLoading = false)),
    shareReplay(1)
  );

  protected meta$ = this.data$.pipe(map(data => data?.meta));
  protected warranties$ = this.data$.pipe(map(data => data?.items ?? []));

  protected hasFilters$ = this.filters$.pipe(
    map(filters => !!(filters.policyId || filters.planId || filters.startDate || filters.endDate))
  );

  protected plans$ = this.filters$.pipe(
    map(filters => this.policies.find(policy => policy.id === filters.policyId)?.plans ?? [])
  );

  protected paginationConfig$ = this.meta$.pipe(
    map(
      meta =>
        <PaginationConfig>{
          itemsPerPage: meta?.itemsPerPage ?? 10,
          currentPage: meta?.currentPage ?? 1,
          totalItems: meta?.totalItems ?? 0,
          pageSizes: [10, 20, 50],
        }
    )
  );

  protected selection$ = this.selected$.pipe(
    map(selected => {
      const warranties = [...selected.values()];
      const amount = warranties.reduce((sum, warranty) => sum + Number(warranty.netAmountDue ?? 0), 0);

      // `amount` in cents for the payment dialog; `total` formatted for the bar.
      return { count: warranties.length, amount, total: formatCents(amount) };
    })
  );

  // Select All covers the current page, which is all the table knows about.
  protected pageSelection$ = combineLatest([this.warranties$, this.selected$]).pipe(
    map(([warranties, selected]) => {
      const payable = warranties.filter(isPayable);
      const selectedCount = payable.filter(warranty => selected.has(warranty.id)).length;

      return {
        hasPayableRows: payable.length > 0,
        isAllSelected: payable.length > 0 && selectedCount === payable.length,
        isSomeSelected: selectedCount > 0 && selectedCount < payable.length,
      };
    })
  );

  protected toggleWarranty(warranty: SelectedWarranty) {
    if (!isPayable(warranty) || this.openBatch$.value) return;

    const selected = new Map(this.selected$.value);

    if (!selected.delete(warranty.id)) {
      if (selected.size >= this.maxBatchSize) {
        this.limitReached$.next(true);
        return;
      }

      selected.set(warranty.id, warranty);
    }

    this.limitReached$.next(false);
    this.selected$.next(selected);
  }

  protected toggleAllOnPage(warranties: UnfundedWarranty[] | null) {
    if (this.openBatch$.value) return;

    const payable = (warranties ?? []).filter(isPayable);
    const selected = new Map(this.selected$.value);

    if (payable.length && payable.every(warranty => selected.has(warranty.id))) {
      payable.forEach(warranty => selected.delete(warranty.id));
      this.limitReached$.next(false);
    } else {
      // Fill up to the cap rather than refusing the whole page, then say the limit was hit.
      const room = this.maxBatchSize - selected.size;
      const missing = payable.filter(warranty => !selected.has(warranty.id));

      missing.slice(0, Math.max(room, 0)).forEach(warranty => selected.set(warranty.id, warranty));
      this.limitReached$.next(missing.length > room);
    }

    this.selected$.next(selected);
  }

  protected clearSelection() {
    this.limitReached$.next(false);
    this.selected$.next(new Map());
  }

  protected async proceedToPayment() {
    const warrantyIds = [...this.selected$.value.keys()];
    if (!this.distributorId || !warrantyIds.length || this.isSubmitting || this.openBatch$.value) return;

    this.isSubmitting = true;
    this.submitError$.next(null);

    try {
      await this.batchSvc.create(this.distributorId, warrantyIds);
      // Re-read rather than trust the request: the locked view must show what the server stored.
      await this.loadOpenBatch(this.distributorId);
    } catch (error) {
      this.submitError$.next(describeBatchPaymentError(error, GENERIC_ERROR));
    } finally {
      this.isSubmitting = false;
    }
  }

  protected openPaymentDialog() {
    this.submitError$.next(null);
    this.paymentDialog?.open();
  }

  /**
   * Re-read on every close, not just a successful one. Paying claims the batch as SUBMITTED before it
   * charges and keeps that claim when the charge fails, so a dismissed failure would otherwise leave
   * this page still offering Change Selection on a batch the server has already locked.
   */
  protected async onPaymentDialogClosed(paid: boolean) {
    // Funding happens asynchronously once the charge settles, so re-read rather than assume.
    await this.loadOpenBatch(this.distributorId);

    // Only a successful charge takes warranties out of the list; a failure leaves the page as it was.
    if (paid) {
      // The funded warranties are gone from the list, so keeping them selected would show a stale total.
      this.selected$.next(new Map());
      this.pagination$.next({ ...this.pagination$.value, page: 1 });
    }
  }

  protected async cancelBatch() {
    const batch = this.openBatch$.value;
    if (!batch || this.isSubmitting) return;

    this.isSubmitting = true;
    this.submitError$.next(null);

    try {
      await this.batchSvc.cancel(batch.id);
      // The warranties never left the list — they stay pending — so only the lock has to go.
      this.openBatch$.next(null);
      this.selected$.next(new Map());
    } catch (error) {
      this.submitError$.next(describeBatchPaymentError(error, GENERIC_ERROR));
    } finally {
      this.isSubmitting = false;
    }
  }

  private async loadOpenBatch(distributorId: string | null) {
    if (this.openBatch$.value?.distributorId !== distributorId) this.openBatch$.next(null);
    if (!distributorId) return;

    try {
      const batch = await this.batchSvc.retrieveOpen(distributorId);

      // Nothing cancels this request, so a seller switched mid-flight must not be handed the old batch.
      if (distributorId !== this.distributorId) return;

      this.applyOpenBatch(batch);
    } catch {
      // The list still works without it; the seller just cannot see their draft.
      if (distributorId === this.distributorId) this.openBatch$.next(null);
    }
  }

  // Hydrates from amountApplied, the figure the batch will actually charge, not today's net.
  private applyOpenBatch(batch: BatchPayment.Model | null) {
    this.openBatch$.next(batch);
    if (!batch) return;

    const selected = new Map<string, SelectedWarranty>(
      (batch.warranties ?? []).map(row => [
        row.warrantyId,
        {
          id: row.warrantyId,
          contractNumber: row.warranty?.contractNumber ?? '',
          netAmountDue: row.amountApplied,
        },
      ])
    );

    this.limitReached$.next(false);
    this.selected$.next(selected);
  }

  protected onSortChanged(sort: SortParams) {
    const sortBy = sort?.sortBy ? SORT_KEY_BY_HEADER[sort.sortBy] : undefined;

    this.resetToFirstPage();
    this.sort$.next(sortBy && sort?.sortOrder ? { sortBy, sortOrder: sort.sortOrder } : {});
  }

  protected onPolicyChange(policyId: unknown) {
    // Plans belong to a policy, so a policy change invalidates the selected plan.
    this.updateFilters({ policyId: typeof policyId === 'string' ? policyId : null, planId: null });
  }

  protected onPlanChange(planId: unknown) {
    this.updateFilters({ planId: typeof planId === 'string' ? planId : null });
  }

  protected onDateRangeChange(dateRange: DateRangeInput) {
    if (dateRange instanceof Date) {
      this.updateFilters({ startDate: dateRange, endDate: dateRange });
    } else if (dateRange && 'startDate' in dateRange) {
      this.updateFilters({ startDate: dateRange.startDate ?? null, endDate: dateRange.endDate ?? null });
    } else {
      this.updateFilters({ startDate: null, endDate: null });
    }
  }

  protected clearFilters() {
    this.resetToFirstPage();
    this.filters$.next(NO_FILTERS);
  }

  private updateFilters(patch: Partial<UnfundedFilters>) {
    this.resetToFirstPage();
    this.filters$.next({ ...this.filters$.value, ...patch });
  }

  // A different result set invalidates the current page offset.
  private resetToFirstPage() {
    if (this.pagination$.value.page !== 1) this.pagination$.next({ ...this.pagination$.value, page: 1 });
  }

  private async loadFilterPolicies() {
    this.policies = [];
    this.filters$.next(NO_FILTERS);
    // A batch belongs to one seller, so a seller change invalidates the selection.
    this.selected$.next(new Map());

    try {
      const response = await this.unfundedSvc.listFilterPolicies();
      this.policies = (response?.items ?? []).sort((a, b) => a.title.localeCompare(b.title));
    } catch {
      // Filters degrade to unavailable; the list itself still works.
      this.policies = [];
    }
  }

  ngOnDestroy() {
    this.selected$.complete();
    this.limitReached$.complete();
    this.openBatch$.complete();
    this.submitError$.complete();
    this.error$.complete();
    this.pagination$.complete();
    this.sort$.complete();
    this.filters$.complete();
    this.distributorId$.complete();
  }
}
